/**
 * Cola de prioridad basada en un montículo binario mínimo (Min-Heap).
 * El elemento con menor valor según el comparador siempre sale primero.
 * Inserción y extracción en O(log n).
 *
 * @template T - El tipo de los valores almacenados.
 *
 * @example Min-heap de números (por defecto)
 * ```ts
 * const pq = new PriorityQueue<number>()
 * pq.enqueue(5); pq.enqueue(1); pq.enqueue(3)
 * pq.dequeue() // → 1
 * ```
 *
 * @example FIFO estable entre iguales (stable: true)
 * ```ts
 * interface Patient { name: string; priority: number }
 * const hospital = new PriorityQueue<Patient>(
 *   (a, b) => a.priority - b.priority,
 *   { stable: true }
 * )
 * hospital.enqueue({ name: 'Ana',  priority: 2 })
 * hospital.enqueue({ name: 'Luis', priority: 1 })
 * hospital.enqueue({ name: 'María', priority: 2 })
 * hospital.dequeue()?.name // → 'Luis'   (prioridad 1)
 * hospital.dequeue()?.name // → 'Ana'    (prioridad 2, llegó primero)
 * hospital.dequeue()?.name // → 'María'  (prioridad 2, llegó segunda)
 * ```
 */
export class PriorityQueue<T> {
  // Internal heap stores wrapped entries to support stable tiebreaking.
  readonly #heap: Array<{ value: T; seq: number }> = []
  readonly #comparator: (a: T, b: T) => number
  readonly #stable: boolean
  #seq = 0

  /**
   * @param comparator - Función de comparación opcional (a, b) => number.
   *   Negativo = a tiene mayor prioridad; cero = igual; positivo = b tiene mayor prioridad.
   *   Por defecto ordena ascendentemente (min-heap natural).
   * @param options.stable - Si es true, elementos con igual prioridad salen en orden
   *   de inserción (FIFO). Por defecto false.
   */
  constructor(
    comparator?: (a: T, b: T) => number,
    options?: { stable?: boolean }
  ) {
    this.#comparator = comparator ?? ((a, b) => {
      if (a < b) return -1
      if (a > b) return 1
      return 0
    })
    this.#stable = options?.stable ?? false
  }

  /** Returns true if the priority queue was created in stable mode. */
  get stable(): boolean {
    return this.#stable
  }

  /**
   * Inserta un valor manteniendo la propiedad del heap.
   */
  enqueue(value: T): void {
    this.#heap.push({ value, seq: this.#seq++ })
    this.#bubbleUp(this.#heap.length - 1)
  }

  /**
   * Elimina y retorna el elemento de mayor prioridad (mínimo por defecto).
   * En modo stable, desempata por orden de inserción (FIFO).
   * @returns El elemento, o undefined si la cola está vacía.
   */
  dequeue(): T | undefined {
    if (this.#heap.length === 0) return undefined
    const top = this.#heap[0].value
    const last = this.#heap.pop()!
    if (this.#heap.length > 0) {
      this.#heap[0] = last
      this.#bubbleDown(0)
    }
    return top
  }

  /**
   * Retorna el elemento de mayor prioridad sin eliminarlo.
   */
  peek(): T | undefined {
    return this.#heap[0]?.value
  }

  /**
   * Indica si la cola está vacía.
   */
  isEmpty(): boolean {
    return this.#heap.length === 0
  }

  /**
   * Número de elementos en la cola.
   */
  get size(): number {
    return this.#heap.length
  }

  /**
   * Retorna los elementos en orden interno del heap (no necesariamente ordenados).
   */
  toArray(): T[] {
    return this.#heap.map(e => e.value)
  }

  /**
   * Vacía la cola completamente.
   */
  clear(): void {
    this.#heap.length = 0
    this.#seq = 0
  }

  // ── Comparación interna ────────────────────────────────────────────────

  /**
   * Compara dos heap entries usando el comparador del usuario.
   * En modo stable, el número de secuencia actúa como desempate FIFO
   * cuando el comparador retorna 0.
   */
  #compare(
    a: { value: T; seq: number },
    b: { value: T; seq: number }
  ): number {
    const result = this.#comparator(a.value, b.value)
    if (result !== 0 || !this.#stable) return result
    return a.seq - b.seq  // FIFO tiebreaker: menor seq = llegó antes
  }

  // ── Índices de nodos padre e hijos ────────────────────────────────────

  #parent(i: number): number {
    return Math.floor((i - 1) / 2)
  }

  #left(i: number): number {
    return 2 * i + 1
  }

  #right(i: number): number {
    return 2 * i + 2
  }

  #swap(i: number, j: number): void {
    const tmp = this.#heap[i]
    this.#heap[i] = this.#heap[j]
    this.#heap[j] = tmp
  }

  /**
   * Retorna el índice del hijo con mayor prioridad entre i, left y right.
   */
  #highestPriority(i: number, n: number): number {
    let best = i
    const left = this.#left(i)
    const right = this.#right(i)
    if (left < n && this.#compare(this.#heap[left], this.#heap[best]) < 0) best = left
    if (right < n && this.#compare(this.#heap[right], this.#heap[best]) < 0) best = right
    return best
  }

  #bubbleUp(i: number): void {
    while (i > 0) {
      const parent = this.#parent(i)
      if (this.#compare(this.#heap[i], this.#heap[parent]) < 0) {
        this.#swap(i, parent)
        i = parent
      } else {
        break
      }
    }
  }

  #bubbleDown(i: number): void {
    const n = this.#heap.length
    let next = this.#highestPriority(i, n)
    while (next !== i) {
      this.#swap(i, next)
      i = next
      next = this.#highestPriority(i, n)
    }
  }
}
